import type { TEventColor } from '@/features/calendar/types'
import type { ICalendarEvent } from '@/features/calendar/interfaces'

import { cva } from 'class-variance-authority'
import { format } from 'date-fns'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

const bookingBulletVariants = cva('size-2 rounded-full', {
  variants: {
    color: {
      blue: 'bg-blue-600 dark:bg-blue-500',
      green: 'bg-green-600 dark:bg-green-500',
      red: 'bg-red-600 dark:bg-red-500',
      yellow: 'bg-yellow-600 dark:bg-yellow-500',
      purple: 'bg-purple-600 dark:bg-purple-500',
      gray: 'bg-gray-600 dark:bg-gray-500',
      orange: 'bg-orange-600 dark:bg-orange-500',
    },
  },
  defaultVariants: {
    color: 'blue',
  },
})

export function BookingBullet({ 
  color, 
  className, 
  training 
}: { 
  color: TEventColor
  className: string
  training?: ICalendarEvent 
}) {
  const bulletElement = <div className={cn(bookingBulletVariants({ color, className }))} />
  
  if (!training) {
    return bulletElement
  }

  const tooltipContent = (
    <div className="max-w-xs space-y-1">
      <div className="font-semibold text-sm">{training.title}</div>
      <div className="text-xs opacity-90">
        <div>📅 {format(new Date(training.startAt), 'MMM dd, yyyy')}</div>
        <div>🕐 {format(new Date(training.startAt), 'HH:mm')} - {format(new Date(training.endAt), 'HH:mm')}</div>
        {training.location && <div>📍 {training.location}</div>}
        {training.category && <div>🏷️ {training.category}</div>}
      </div>
    </div>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {bulletElement}
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-black text-white">
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  )
}
